import { useMemo } from 'react';
import Panel from '@adsk/alloy-react-panel';
import TextInput from '@adsk/alloy-react-text-input';
import { MagnifyingGlassIcon } from '@adsk/alloy-react-icon';
import { useUsers } from '../../../providers';
import AssignToDropDown from './FilterComponents/AssignToDropDown';
import { featureFlags, translate, EnumDropdownOption, getIdsFromAssignees } from '../../../helpers';
import {
  getStartEndDateFromFilter,
  updateFilterDateField,
  getEnumSelectedOptions,
  getUpdateFilters,
  sortAlphabeticallyByName,
  filterUsersArray,
  getDropdownOptionValues,
} from './FilterComponents/helpers/filterHelpers';
import { StatusDropDown } from '../../../components/Dropdowns/StatusDropDown';
import { useDueDateTimezone, useGetRfiTypes } from '../../../hooks';
import {
  AssigneePicker,
  CategoryField,
  CommonFormField,
  CostImpactMultiSelectField,
  CreatedAtFilterField,
  DisciplineField,
  DueDateFilterField,
  LocationPicker,
  LocationPickerField,
  MultiSelectDropdown,
  PriorityMultiSelectField,
  ScheduleImpactMultiSelectField,
} from '../../../components';
import { Assignee, AssigneeType } from '../../../sdk';
import { FEATURE_FLAGS } from '../../../constants';
import type { Filters, FiltersPanelProps } from './FiltersPanelTypes';

const nowInUtcIsoString = new Date().toISOString();

const FiltersPanel = ({ filters, setFilters, onClose, open }: FiltersPanelProps) => {
  const { data: users } = useUsers();
  const { dueDateTimeZone } = useDueDateTimezone(nowInUtcIsoString);

  const updateFilters = <T extends keyof Filters>(key: T, value: Filters[T]): void => {
    const newFilters: Filters = { ...filters };
    if (!value || (Array.isArray(value) && value.length === 0)) {
      // Need to delete the entire key from the object because otherwise
      // ReferencePicker will understand it as if the filter exists
      // (even if the value is undefined or null)
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    setFilters(newFilters);
  };

  const sortedUsers = sortAlphabeticallyByName(filterUsersArray(users));
  const userOptions: Assignee[] = sortedUsers.map((user) => ({
    id: user.autodeskId,
    type: AssigneeType.User,
  }));

  const dueDateValue = getStartEndDateFromFilter(filters.dueDate, dueDateTimeZone);
  const createdAtDateValue = getStartEndDateFromFilter(filters.createdAt);
  const { data: rfiTypes = [] } = useGetRfiTypes();

  const shouldRenderExternalIdFilter = featureFlags.get(FEATURE_FLAGS.EXTERNAL_ID_FILTER);

  const rfiTypesOptions: EnumDropdownOption[] = useMemo(
    () =>
      rfiTypes
        .filter((rfiType) => rfiType.status === 'active')
        .map((rfiType) => ({
          value: rfiType.id,
          label: rfiType.name,
          key: rfiType.id,
        })),
    [rfiTypes],
  );

  const selectedAssignees = useMemo(
    () => filters?.assignedTo?.map((id) => ({ id, type: AssigneeType.User })) ?? [],
    [filters?.assignedTo],
  );

  const selectedCreatedBy = useMemo(
    () => filters?.createdBy?.map((id) => ({ id, type: AssigneeType.User })) ?? [],
    [filters?.createdBy],
  );

  return (
    <Panel title={translate('rpPlugin.filter.filters')} open={open} onClose={onClose}>
      <Panel.Body>
        <StatusDropDown
          selectedStatuses={filters.status}
          onChange={(value) => updateFilters('status', getDropdownOptionValues(value))}
        />
        <AssignToDropDown
          assigneesOptions={userOptions}
          selectedAssignees={selectedAssignees}
          onChange={(value) => updateFilters('assignedTo', getIdsFromAssignees(value))}
        />
        <DueDateFilterField
          onChange={updateFilterDateField({
            key: 'dueDate',
            updateFilters,
            timeZone: dueDateTimeZone,
          })}
          startDate={dueDateValue.startDate}
          endDate={dueDateValue.endDate}
        />

        <CommonFormField label={translate('rpPlugin.new.rfiType')}>
          <MultiSelectDropdown
            selectedItems={getEnumSelectedOptions(filters.rfiTypeId, rfiTypesOptions)}
            options={rfiTypesOptions}
            onChange={(value) => {
              updateFilters('rfiTypeId', getDropdownOptionValues(getUpdateFilters(value)));
            }}
            placeholder={translate('rpPlugin.filter.rfiType')}
            clearable
          />
        </CommonFormField>

        <LocationPickerField
          selectedLocationIds={filters.locations}
          onChangeLocationIds={(ids) => updateFilters('locations', ids)}
          action={LocationPicker.ACTIONS.FILTER}
        />
        <CostImpactMultiSelectField
          onChange={(value) => updateFilters('costImpact', getDropdownOptionValues(value))}
          value={filters.costImpact}
        />
        <ScheduleImpactMultiSelectField
          onChange={(value) => updateFilters('scheduleImpact', getDropdownOptionValues(value))}
          value={filters.scheduleImpact}
        />
        <PriorityMultiSelectField
          onChange={(value) => updateFilters('priority', getDropdownOptionValues(value))}
          value={filters.priority}
        />
        <DisciplineField
          onChange={(value) => updateFilters('discipline', getDropdownOptionValues(value))}
          value={filters.discipline}
        />
        <CategoryField
          value={filters.category}
          onChange={(value) => updateFilters('category', getDropdownOptionValues(value))}
        />
        <CommonFormField label={translate('rpPlugin.filter.createdBy')}>
          <AssigneePicker
            onChange={(value) => updateFilters('createdBy', getIdsFromAssignees(value))}
            selectedAssignees={selectedCreatedBy}
            assigneesOptions={userOptions}
          />
        </CommonFormField>
        <CreatedAtFilterField
          onChange={updateFilterDateField({ key: 'createdAt', updateFilters })}
          startDate={createdAtDateValue.startDate}
          endDate={createdAtDateValue.endDate}
        />
        {shouldRenderExternalIdFilter && (
          <CommonFormField label={translate('rpPlugin.filter.externalId')}>
            <TextInput
              placeholder={translate('rpPlugin.filter.searchByExternalId')}
              renderLeftElement={MagnifyingGlassIcon.renderIcon}
              value={filters.reference || ''}
              onChange={(event) => updateFilters('reference', event.target.value)}
            />
          </CommonFormField>
        )}
      </Panel.Body>
    </Panel>
  );
};

export default FiltersPanel;
